import type { RequestOptions } from '../client.js';
import {
  type PortfolioData,
  type SolanaPortfolioData,
  type LogsData,
  type TransfersData,
  type TraceTransactionData,
  getLogsParamsSchema,
  getTransfersParamsSchema,
  getReceiptParamsSchema,
  getPortfolioParamsSchema,
  getChainHealthParamsSchema,
  traceTransactionParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { ChainSlug, EvmChainSlug, StarknetChainSlug, SolanaChainSlug } from '../types/chains.js';
import type { HistoricalQueryOptions } from '../types/client.js';
import type {
  ChainHealthResult,
  LogsFilter,
  TransfersFilter,
  ReceiptResult,
} from '../types/index.js';
import { parseParams } from '../internals/validation.js';
import { splitHistoricalArgs } from '../internals/utils.js';
import { ChainNamespace } from './base.js';

/** Logs, transfers, receipts, portfolio, and chain health */
export class DataNamespace extends ChainNamespace {
  /** Query event logs. EVM + Starknet. */
  async getLogs(
    chain: EvmChainSlug | StarknetChainSlug | undefined,
    filter?: LogsFilter,
    options?: RequestOptions,
  ): Promise<LogsData> {
    const validated = parseParams(getLogsParamsSchema, {
      chain: this.resolve(chain),
      address: filter?.address,
      topic0: filter?.topic0,
      fromBlock: filter?.fromBlock !== undefined ? String(filter.fromBlock) : undefined,
      toBlock: filter?.toBlock !== undefined ? String(filter.toBlock) : undefined,
      cursor: filter?.cursor,
    });
    return this.client.jsonRpc<LogsData>('getLogs', validated, options);
  }

  /** Get token transfers for an address. EVM only. */
  async getTransfers(
    chain: EvmChainSlug | undefined,
    address: string,
    filter?: TransfersFilter,
    options?: RequestOptions,
  ): Promise<TransfersData> {
    const validated = parseParams(getTransfersParamsSchema, {
      chain: this.resolve(chain),
      address,
      fromBlock: filter?.fromBlock !== undefined ? String(filter.fromBlock) : undefined,
      toBlock: filter?.toBlock !== undefined ? String(filter.toBlock) : undefined,
      cursor: filter?.cursor,
    });
    return this.client.jsonRpc<TransfersData>('getTransfers', validated, options);
  }

  /** Get a transaction receipt by hash. */
  async getReceipt(
    chain: ChainSlug | undefined,
    hash: string,
    options?: RequestOptions,
  ): Promise<ReceiptResult> {
    const validated = parseParams(getReceiptParamsSchema, { chain: this.resolve(chain), hash });
    return this.client.jsonRpc<ReceiptResult>('getReceipt', validated, options);
  }

  /** Get portfolio for an address on a chain. EVM only. */
  async getPortfolio(
    chain: EvmChainSlug | SolanaChainSlug | undefined,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<PortfolioData | SolanaPortfolioData> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getPortfolioParamsSchema, {
      chain: this.resolve(chain),
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<PortfolioData | SolanaPortfolioData>(
      'getPortfolio',
      validated,
      requestOptions,
    );
  }

  /** Get health status for a specific chain. */
  async getChainHealth(chain?: ChainSlug, options?: RequestOptions): Promise<ChainHealthResult> {
    const validated = parseParams(getChainHealthParamsSchema, { chain: this.resolve(chain) });
    return this.client.jsonRpc<ChainHealthResult>('getChainHealth', validated, options);
  }

  /**
   * Trace a transaction's internal execution. EVM + Starknet.
   * Returns call traces showing internal calls, delegatecalls, and value transfers.
   */
  async traceTransaction(
    chain: EvmChainSlug | StarknetChainSlug | undefined,
    hash: string,
    options?: RequestOptions,
  ): Promise<TraceTransactionData> {
    const validated = parseParams(traceTransactionParamsSchema, {
      chain: this.resolve(chain),
      hash,
    });
    return this.client.jsonRpc<TraceTransactionData>('traceTransaction', validated, options);
  }
}
